import { cloneDeep, get, unset } from 'lodash';
import type { Form } from '@formily/core';
import { ComponentType, type IFormilySchema } from '../types';
import { findFormComponent } from './findFormComponent';

/**
 * `childSchema` is stored escaped (see `escapeFormItemsSchemas`), so
 * `properties.childSchemaForm` can still be a JSON string at this point.
 */
const parseChildSchemaForm = (
  childSchema?: IFormilySchema
): IFormilySchema | undefined => {
  const childSchemaForm = childSchema?.properties?.childSchemaForm;
  if (typeof childSchemaForm !== 'string') return childSchema;

  return {
    ...childSchema,
    properties: {
      ...childSchema?.properties,
      childSchemaForm: JSON.parse(childSchemaForm),
    },
  } as IFormilySchema;
};

const getRowProperties = (
  childSchema?: IFormilySchema
): Record<string, IFormilySchema> =>
  findFormComponent(parseChildSchemaForm(childSchema))?.properties ?? {};

const stripRow = (
  row: unknown,
  properties: Record<string, IFormilySchema>
): void => {
  if (!row || typeof row !== 'object') return;

  Object.entries(properties).forEach(([name, fieldSchema]) => {
    if (fieldSchema.type === 'void' && fieldSchema.properties) {
      stripRow(row, fieldSchema.properties);
      return;
    }

    if ((fieldSchema as any)['x-data']?.skipOnSubmit) {
      unset(row, name);
      return;
    }

    if (fieldSchema['x-component'] === ComponentType.FormArrayTable) {
      const nestedChildSchema = (fieldSchema as any)['x-component-props']
        ?.childSchema;
      const nestedRows = get(row, name);
      if (Array.isArray(nestedRows) && nestedChildSchema) {
        const nestedProperties = getRowProperties(nestedChildSchema);
        nestedRows.forEach(nestedRow => stripRow(nestedRow, nestedProperties));
      }
    }
  });
};

/**
 * Returns form values with fields marked `x-data: { skipOnSubmit: true }`
 * excluded — including ones nested inside `FormArrayTable` rows, at any
 * depth.
 *
 * A row's own `skipOnSubmit` field is a live Formily field only while its
 * row form (`AddRowForm`) is mounted; once saved, the row is merged back
 * into the array field's value as plain data, so it can no longer be
 * checked via `form.fields`. To reach it anyway, every `FormArrayTable`
 * field's `componentProps.childSchema` (the row schema, always available on
 * the field itself) is walked directly and `skipOnSubmit` paths are
 * stripped from each row.
 */
export const stripSkipOnSubmitFields = (
  form: Form
): Record<string, unknown> => {
  const values = cloneDeep(form.getFormState().values);

  Object.values(form.fields).forEach(field => {
    const name = field.props.name as string | undefined;
    if (!name) return;

    if (field.data?.skipOnSubmit) {
      unset(values, name);
      return;
    }

    const childSchema = (field as any).componentProps?.childSchema as
      | IFormilySchema
      | undefined;
    if (!childSchema) return;

    const rows = get(values, name);
    if (!Array.isArray(rows)) return;

    const properties = getRowProperties(childSchema);
    rows.forEach(row => stripRow(row, properties));
  });

  return values;
};
